import React, { useEffect, useState } from 'react'
import { Box, Card, FormControl, InputLabel, MenuItem, Select, CircularProgress, TextField } from '@mui/material'
import Dropdown from '../components/DropDown';
import ExpendedTableView from '../components/ExpendedTableView'
import { getCropsAndVarieties, getAllMachine, getScanHistory } from '../utils/api';
import { onGetFormatedDateRange } from '../utils/common';

const ACCENT_BLUE = 'rgb(34, 93, 153)';
const ACCENT_ORANGE = 'rgb(217, 108, 60)';

const cardShell = {
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(12, 61, 110, 0.05)',
    border: '1px solid rgba(34, 93, 153, 0.06)',
    backgroundColor: '#fff',
}

const selectSx = {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 93, 153, 0.15)' },
}

const Scan = () => {
    const company_id = localStorage.getItem("company_id");

    const [allCropVarieties, setAllCropVarieties] = useState([])
    const [uniqueCrops, setUniqueCrops] = useState([])
    const [selectedCropNames, setSelectedCropNames] = useState([])
    const [varietyOptions, setVarietyOptions] = useState([])
    const [selectedVarietyIds, setSelectedVarietyIds] = useState([])

    const [machineList, setMachineList] = useState([])
    const [selectedMachineIds, setSelectedMachineIds] = useState([])

    const [dateOption, setDateOption] = useState('this_week')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    const [loading, setLoading] = useState(true)
    const [scanData, setScanData] = useState([])

    useEffect(() => {
        (async () => {
            try {
                setLoading(true)
                const [cropsRes, machinesRes] = await Promise.all([
                    getCropsAndVarieties(company_id),
                    getAllMachine(company_id),
                ])
                const cropVarietyData = cropsRes?.data?.data || []
                setAllCropVarieties(cropVarietyData)

                const uniqueCropList = [...new Map(cropVarietyData.map(c => [c.crop_name, c])).values()]
                setUniqueCrops(uniqueCropList)
                setSelectedCropNames(uniqueCropList.map(c => c.crop_name))

                setVarietyOptions(cropVarietyData)
                setSelectedVarietyIds(cropVarietyData.map(v => v.variety_id))

                const machineData = machinesRes?.data?.data || []
                setMachineList(machineData)
                setSelectedMachineIds(machineData.map(m => m.machine_id))

                const { start_date, end_date } = onGetFormatedDateRange('this_week')
                await fetchScanData(cropVarietyData, machineData.map(m => m.machine_id), start_date, end_date)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const handleCropChange = (next) => {
        setSelectedCropNames(next)
        const varieties = allCropVarieties.filter(c => next.includes(c.crop_name))
        setVarietyOptions(varieties)
        setSelectedVarietyIds(varieties.map(v => v.variety_id))
    }

    const resolveDateRange = () => {
        if (dateOption === 'custom' && customStart && customEnd) {
            return { start_date: customStart, end_date: customEnd }
        }
        return onGetFormatedDateRange(dateOption)
    }

    const fetchScanData = async (varietyRows, machineIds, start_date, end_date) => {
        if (!varietyRows?.length || !machineIds?.length) return
        setLoading(true)
        try {
            const combinations = varietyRows.flatMap(({ crop_id, variety_id }) =>
                machineIds.map((machine_id) => ({ crop_id, variety_id, machine_id }))
            )
            const res = await getScanHistory({
                crop_variety_machine: combinations,
                start_date,
                end_date,
                company_id,
            })
            setScanData(res?.data?.data || [])
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleViewData = () => {
        const rows = varietyOptions.filter(v => selectedVarietyIds.includes(v.variety_id))
        const { start_date, end_date } = resolveDateRange()
        fetchScanData(rows, selectedMachineIds, start_date, end_date)
    }

    return (
        <Box sx={{ p: 3, backgroundColor: '#eef4fb', minHeight: '100vh' }}>

            <Card sx={{ ...cardShell, p: '22px', mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>

                    <Dropdown
                        label="Crop"
                        minWidth={200}
                        options={uniqueCrops}
                        getKey={(c) => c.crop_name}
                        getLabel={(c) => c.crop_name}
                        selectedKeys={selectedCropNames}
                        onChange={handleCropChange}
                        allLabel="All crops"
                    />

                    <Dropdown
                        label="Variety"
                        minWidth={220}
                        options={varietyOptions}
                        getKey={(v) => v.variety_id}
                        getLabel={(v) => v.variety_name}
                        getChipLabel={(v) => `${v.crop_name}: ${v.variety_name}`}
                        getGroupLabel={(v) => v.crop_name}
                        selectedKeys={selectedVarietyIds}
                        onChange={setSelectedVarietyIds}
                        allLabel="All varieties"
                    />

                    <Dropdown
                        label="Machine"
                        minWidth={220}
                        options={machineList}
                        getKey={(m) => m.machine_id}
                        getLabel={(m) => m.machine_name}
                        selectedKeys={selectedMachineIds}
                        onChange={setSelectedMachineIds}
                        allLabel="All machines"
                    />

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Date range</InputLabel>
                        <Select label="Date range" value={dateOption} onChange={(e) => setDateOption(e.target.value)} sx={selectSx}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="this_week">This Week</MenuItem>
                            <MenuItem value="this_month">This Month</MenuItem>
                            <MenuItem value="this_year">This Year</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                    </FormControl>

                    {dateOption === 'custom' && (
                        <>
                            <TextField
                                type="date" size="small" label="Start" InputLabelProps={{ shrink: true }}
                                value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': selectSx }}
                            />
                            <TextField
                                type="date" size="small" label="End" InputLabelProps={{ shrink: true }}
                                value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': selectSx }}
                            />
                        </>
                    )}

                    <button
                        onClick={handleViewData}
                        style={{
                            background: `linear-gradient(135deg, ${ACCENT_BLUE}, rgb(24, 73, 123))`,
                            color: '#fff', border: 'none', borderRadius: '10px',
                            padding: '10px 26px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
                            marginLeft: 'auto', boxShadow: '0 6px 16px rgba(34, 93, 153, 0.3)',
                        }}
                    >
                        View Data
                    </button>
                </Box>
            </Card>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress sx={{ color: ACCENT_ORANGE }} />
                </Box>
            ) : (
                <Card sx={{ ...cardShell, overflow: 'hidden' }}>
                    {scanData.length > 0
                        ? <ExpendedTableView dataList={scanData} sourceKey="scan_history" />
                        : <Box sx={{ py: 6, textAlign: 'center', color: 'rgb(170,170,180)' }}>There are no records to display</Box>}
                </Card>
            )}
        </Box>
    )
}

export default Scan